import type { UnseenWalletMovement } from '@/types/userProgress'

import type { RewardGainBatch, RewardGainItem } from '../model/types'

/**
 * Quantos badges individuais um lote exibe antes de colapsar o resto num
 * agregado. Três é o limite do que se lê em sequência sem virar fila de espera.
 */
export const REWARD_GAIN_BADGE_CAP = 3

/** Quanto tempo cada badge fica visível, em ms. */
export const REWARD_GAIN_BADGE_DURATION_MS = 1800

/** Intervalo entre a saída de um badge e a entrada do próximo, em ms. */
export const REWARD_GAIN_BADGE_STAGGER_MS = 300

const amount = (value: number | null | undefined) => Number(value ?? 0)

const isReward = (movement: UnseenWalletMovement) =>
  movement.kind === 'deposit' &&
  (amount(movement.amount_points) > 0 || amount(movement.amount_coins) > 0)

/**
 * Monta o lote de exibição a partir dos movimentos não vistos de um ciclo.
 *
 * Módulo puro de propósito: o vitest do projeto roda em ambiente `node`, então
 * a decisão de o que exibir precisa ser testável sem montar componente.
 *
 * @param movements movimentos não vistos entregues pelo payload de progresso,
 *   já recortados e ordenados por id ascendente pelo servidor
 * @param unseenRewardCount contagem de não vistos **elegíveis a badge**, vinda
 *   de `unseen_reward_movements_count` — não do tamanho de `movements`, que
 *   chega truncado pelo teto do servidor
 * @returns o lote, ou `null` quando não há movimento algum
 */
export function buildRewardGainBatch(
  movements: UnseenWalletMovement[],
  unseenRewardCount: number
): RewardGainBatch | null {
  if (movements.length === 0) return null

  // O ACK sai do maior id recebido, elegível ou não. Se saísse do último
  // depósito, um `withdraw` mais recente ficaria eternamente acima do cursor e a
  // janela de não vistos nunca esvaziaria.
  const ackId = movements.reduce((max, movement) => Math.max(max, movement.id), 0)

  const eligible = movements.filter(isReward)

  // Os mais recentes são os que o participante espera ver celebrados; a
  // exibição, porém, é cronológica.
  const items: RewardGainItem[] = eligible
    .slice(-REWARD_GAIN_BADGE_CAP)
    .map(movement => ({
      movementId: movement.id,
      points: amount(movement.amount_points),
      coins: amount(movement.amount_coins)
    }))

  const overflowCount = Math.max(0, unseenRewardCount - items.length)

  return { items, overflowCount, ackId }
}
